import { getRouteApi, Link, notFound } from '@tanstack/react-router';
import { ArrowRight, ExternalLink, MapPin, Phone, Star } from 'lucide-react';
import { graphql, useFragment, usePreloadedQuery } from 'react-relay';

import { useLoadedData } from '@/loader';

import type { DirectoryAgentPage_agent$key } from './__generated__/DirectoryAgentPage_agent.graphql';
import type { DirectoryAgentPageQuery } from './__generated__/DirectoryAgentPageQuery.graphql';
import DirectoryAgentClaimRequestDialog from './components/DirectoryAgentClaimRequestDialog';
import DirectoryAgentSeoHead from './components/DirectoryAgentSeoHead';
import DirectoryFeeBlock from './components/DirectoryFeeBlock';
import DirectoryPageShell from './components/DirectoryPageShell';
import DirectorySourceLinks from './components/DirectorySourceLinks';
import { findTownForAreas } from './utils/town-for-areas';

const routeApi = getRouteApi('/agents/$agentSlug');
const checkedDateFormatter = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

const DirectoryAgentPage = () => {
  const { footerLandingPages, footerLegalLinks, queryData, towns } =
    routeApi.useLoaderData();
  const queryRef = useLoadedData<DirectoryAgentPageQuery>(queryData);
  const data = usePreloadedQuery<DirectoryAgentPageQuery>(
    graphql`
      query DirectoryAgentPageQuery($agentSlug: String!) {
        landlordDirectoryAgent(slug: $agentSlug) {
          ...DirectoryAgentPage_agent
          ...DirectoryAgentSeoHead_agent
        }
      }
    `,
    queryRef,
  );

  if (!data.landlordDirectoryAgent) {
    throw notFound();
  }

  return (
    <DirectoryPageShell
      footerLandingPages={footerLandingPages}
      footerLegalLinks={footerLegalLinks}
    >
      <DirectoryAgentSeoHead
        agent={data.landlordDirectoryAgent}
        towns={towns}
      />
      <DirectoryAgentPageContent
        agent={data.landlordDirectoryAgent}
        towns={towns}
      />
    </DirectoryPageShell>
  );
};

const DirectoryAgentPageContent = ({
  agent: agentKey,
  towns,
}: {
  agent: DirectoryAgentPage_agent$key;
  towns: ReturnType<typeof routeApi.useLoaderData>['towns'];
}) => {
  const agent = useFragment(
    graphql`
      fragment DirectoryAgentPage_agent on LandlordDirectoryAgent {
        id
        slug
        name
        address
        phone
        website
        googleMapsUrl
        googleRating
        googleReviewCount
        trustpilotUrl
        trustpilotRating
        trustpilotReviewCount
        services
        redressScheme
        hasClientMoneyProtection
        enrichedAt
        areas {
          slug
        }
        sources {
          trustpilot
          services
          redressScheme
          clientMoneyProtection
        }
        ...DirectoryFeeBlock_agent
      }
    `,
    agentKey,
  );

  if (!agent.slug) {
    throw notFound();
  }

  const town = findTownForAreas(
    towns,
    agent.areas?.map((area) => area?.slug) ?? [],
  );
  const checkedDate = agent.enrichedAt
    ? checkedDateFormatter.format(new Date(agent.enrichedAt))
    : 'Not recorded';

  return (
    <>
      <section className="flex flex-col gap-7 py-[clamp(40px,7vw,80px)]">
        <nav aria-label="Breadcrumb">
          <ol className="m-0 flex list-none flex-wrap gap-2 p-0 text-sm text-ink-muted">
            <li>
              <Link
                className="text-inherit hover:text-ink"
                to="/letting-agents"
              >
                Letting agents
              </Link>
            </li>
            {town ? (
              <>
                <li aria-hidden="true">/</li>
                <li>
                  <Link
                    className="text-inherit hover:text-ink"
                    params={{ townSlug: town.slug }}
                    to="/letting-agents/$townSlug"
                  >
                    {town.name}
                  </Link>
                </li>
              </>
            ) : null}
            <li aria-hidden="true">/</li>
            <li aria-current="page">{agent.name}</li>
          </ol>
        </nav>

        <div className="grid items-start gap-8 md:grid-cols-[minmax(0,1fr)_minmax(260px,320px)]">
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-center gap-2.5">
              <p className="m-0 text-sm font-medium text-accent-strong">
                Letting agent profile
              </p>
              <span className="rounded-full bg-chip px-2.5 py-1 text-xs text-ink-muted">
                Unclaimed · details unverified
              </span>
            </div>
            <h1 className="m-0 text-[clamp(38px,7vw,64px)] leading-[1.02] font-medium tracking-[-0.045em] text-balance">
              {agent.name}
            </h1>
            <div className="flex flex-col gap-2 text-sm text-ink-soft">
              {agent.address ? (
                <a
                  className="flex items-start gap-2 text-inherit no-underline hover:text-ink"
                  href={agent.googleMapsUrl ?? undefined}
                  rel="noopener"
                  target="_blank"
                >
                  <MapPin
                    aria-hidden="true"
                    className="mt-0.5 size-4 shrink-0"
                  />
                  {agent.address}
                </a>
              ) : null}
              {agent.phone ? (
                <a
                  className="flex items-center gap-2 text-inherit no-underline hover:text-ink"
                  href={`tel:${agent.phone.replaceAll(' ', '')}`}
                >
                  <Phone aria-hidden="true" className="size-4" />
                  {agent.phone}
                </a>
              ) : null}
              {agent.website ? (
                <a
                  className="flex items-center gap-2 text-inherit no-underline hover:text-ink"
                  href={agent.website}
                  rel="nofollow noopener"
                  target="_blank"
                >
                  <ExternalLink aria-hidden="true" className="size-4" />
                  Visit agent website
                </a>
              ) : null}
            </div>
          </div>
          <aside className="surface-card flex flex-col items-start gap-3 p-5">
            <h2 className="m-0 text-base font-medium">
              Work at {agent.name ?? 'this agency'}?
            </h2>
            <p className="m-0 text-sm leading-relaxed text-ink-soft">
              Claim this free profile to correct your details and confirm your
              published fees for landlords comparing agents.
            </p>
            <DirectoryAgentClaimRequestDialog
              agentId={agent.id}
              agentName={agent.name ?? 'this agent'}
              agentSlug={agent.slug}
            />
          </aside>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="surface-card p-5">
            <p className="m-0 text-xs text-ink-muted">Google reviews</p>
            <a
              className="mt-2 flex items-center gap-2 text-xl font-medium text-ink no-underline"
              href={agent.googleMapsUrl ?? undefined}
              rel="noopener"
              target="_blank"
            >
              <Star aria-hidden="true" className="size-5" />
              {formatRating(agent.googleRating, agent.googleReviewCount)}
            </a>
          </div>
          <div className="surface-card p-5">
            <div className="flex items-center justify-between gap-2">
              <p className="m-0 text-xs text-ink-muted">Trustpilot</p>
              <DirectorySourceLinks
                label="Trustpilot rating"
                urls={agent.sources?.trustpilot}
              />
            </div>
            <a
              className="mt-2 flex items-center gap-2 text-xl font-medium text-ink no-underline"
              href={agent.trustpilotUrl ?? undefined}
              rel="nofollow noopener"
              target="_blank"
            >
              <Star aria-hidden="true" className="size-5" />
              {formatRating(
                agent.trustpilotRating,
                agent.trustpilotReviewCount,
              )}
            </a>
          </div>
        </div>
      </section>

      <div className="grid gap-5 pb-[clamp(48px,7vw,88px)] lg:grid-cols-[minmax(0,1.2fr)_minmax(280px,0.8fr)]">
        <DirectoryFeeBlock agent={agent} />
        <div className="flex flex-col gap-5">
          <section className="flex flex-col gap-2 rounded-[20px] bg-accent-tint p-5 md:p-6">
            <h2 className="m-0 text-lg font-medium tracking-[-0.02em]">
              Let through {agent.name ?? 'this agent'}?
            </h2>
            <p className="m-0 text-sm leading-relaxed text-ink-soft">
              Upload your agreement and we’ll compare what you pay against other
              agents near you. Two minutes, no phone calls.
            </p>
            <Link
              className="mt-1 inline-flex items-center gap-1.5 text-sm font-medium text-accent-strong no-underline hover:text-ink"
              to="/start"
            >
              See if you’re on a good deal
              <ArrowRight aria-hidden="true" className="size-4" />
            </Link>
          </section>
          <section className="surface-card flex flex-col gap-4 p-5 md:p-7">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="m-0 text-2xl font-medium tracking-[-0.025em]">
                Services
              </h2>
              <DirectorySourceLinks
                label="Services"
                urls={agent.sources?.services}
              />
            </div>
            {agent.services?.length ? (
              <ul className="m-0 grid gap-2 pl-5 text-sm text-ink-soft">
                {agent.services.map((service) => (
                  <li key={service}>{service}</li>
                ))}
              </ul>
            ) : (
              <p className="m-0 text-sm text-ink-muted">
                No service list was found.
              </p>
            )}
          </section>

          <section className="surface-card flex flex-col gap-4 p-5 md:p-7">
            <h2 className="m-0 text-2xl font-medium tracking-[-0.025em]">
              Compliance
            </h2>
            <div className="flex flex-col gap-3 text-sm">
              <div>
                <p className="m-0 font-medium">Redress scheme</p>
                <div className="mt-1 flex flex-wrap items-center gap-2 text-ink-soft">
                  {formatRedressScheme(agent.redressScheme)}
                  <DirectorySourceLinks
                    label="Redress scheme"
                    urls={agent.sources?.redressScheme}
                  />
                </div>
              </div>
              <div>
                <p className="m-0 font-medium">Client money protection</p>
                <div className="mt-1 flex flex-wrap items-center gap-2 text-ink-soft">
                  {formatClientMoneyProtection(agent.hasClientMoneyProtection)}
                  <DirectorySourceLinks
                    label="Client money protection"
                    urls={agent.sources?.clientMoneyProtection}
                  />
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      <aside className="mb-[clamp(48px,7vw,88px)] rounded-2xl bg-accent-tint p-5 text-sm leading-relaxed text-ink-soft md:p-6">
        <h2 className="m-0 text-base font-medium text-ink">
          Where this information comes from
        </h2>
        <p className="m-0 mt-2">
          We collect public information from agent websites, review platforms,
          Google and compliance directories. The site name beside each item
          links to where we found it. Fees and profile details are unverified
          until the agent confirms them.
        </p>
        <p className="m-0 mt-2 font-medium text-ink">
          Last checked: {checkedDate}
        </p>
      </aside>
    </>
  );
};

const formatRating = (
  rating: number | null | undefined,
  reviewCount: number | null | undefined,
) => {
  if (rating == null) {
    return 'Not found';
  }

  return `${rating.toFixed(1)} · ${reviewCount ?? 0} reviews`;
};

const formatRedressScheme = (scheme: string | null | undefined) => {
  if (!scheme) {
    return 'Not verified';
  }

  return scheme === 'PROPERTY_REDRESS_SCHEME'
    ? 'Property Redress Scheme'
    : 'The Property Ombudsman';
};

const formatClientMoneyProtection = (
  hasClientMoneyProtection: boolean | null | undefined,
) => {
  if (hasClientMoneyProtection == null) {
    return 'Not verified';
  }

  return hasClientMoneyProtection ? 'Found' : 'Not found';
};

export default DirectoryAgentPage;
